import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Avatar,
    Chip,
    Button,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SidebarMenu } from "components";

type Rating = {
    id: string;
    productId: string;
    score: number;
    comment?: string;
    createdAt: string;
    clientId: string;
    clientName: string;
};

type Visit = {
    id: string;
    ratingId?: string;
    clientId: string;
    clientName: string;
    visitDate: string;
    score?: number;
    comment?: string;
};

const mockRatings: Rating[] = [
    { id: "r1", productId: "p1", score: 5, comment: "Excelente", createdAt: "2025-11-26T15:24:00Z", clientId: "CL001", clientName: "Super Galo" },
    { id: "r2", productId: "p1", score: 4, comment: "Muy bueno", createdAt: "2025-11-25T11:12:00Z", clientId: "CL002", clientName: "Tienda Uno" },
    { id: "r3", productId: "p1", score: 3, comment: "Aceptable", createdAt: "2025-11-24T18:05:00Z", clientId: "CL003", clientName: "Mercado Central" },
    { id: "r4", productId: "p2", score: 2, comment: "Malo", createdAt: "2025-11-23T09:30:00Z", clientId: "CL004", clientName: "Abarrotes S.A." },
    { id: "r5", productId: "p1", score: 4, comment: "Bien presentado", createdAt: "2025-11-22T20:45:00Z", clientId: "CL005", clientName: "Mini Market" },
];

const mockVisits: Visit[] = [
    { id: "v1", ratingId: "r1", clientId: "CL001", clientName: "Super Galo", visitDate: "2025-11-26", score: 5, comment: "Foto adjunta" },
    { id: "v2", ratingId: "r2", clientId: "CL002", clientName: "Tienda Uno", visitDate: "2025-11-25", score: 4, comment: "Buen stock" },
    { id: "v3", ratingId: "r3", clientId: "CL003", clientName: "Mercado Central", visitDate: "2025-11-24", score: 3, comment: "Poca visibilidad" },
    { id: "v4", ratingId: "r5", clientId: "CL005", clientName: "Mini Market", visitDate: "2025-11-22", score: 4, comment: "Exposición correcta" },
];

const ProductDetailReport: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();

    // Filtrar datos por productId (si no viene, usar productId "p1" por defecto)
    const pid = productId ?? "p1";
    const ratings = mockRatings.filter((r) => r.productId === pid);
    const visits = mockVisits.filter((v) => ratings.some((r) => r.id === v.ratingId) || v.clientId);

    const stats = useMemo(() => {
        const total = ratings.length;
        const avg = total === 0 ? 0 : ratings.reduce((s, r) => s + r.score, 0) / total;
        const min = total === 0 ? 0 : Math.min(...ratings.map((r) => r.score));
        const max = total === 0 ? 0 : Math.max(...ratings.map((r) => r.score));
        return {
            total,
            avg: Math.round(avg * 10) / 10,
            min,
            max,
        };
    }, [ratings]);

    return (
        <div className="w-full h-screen grid grid-cols-12">
            <div className="col-span-2 h-screen sticky top-0 border-r-2 border-solid border-light">
                <SidebarMenu pageName="on6" />
            </div>

            <div className="col-span-10 h-screen overflow-auto p-4">
                <Box sx={{ width: "100%", p: 2 }}>
                    <Typography variant="h6" mb={2}>
                        Detalle de calificaciones — Producto {pid}
                    </Typography>

                    <Grid container spacing={2} mb={3}>
                        <Grid size={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Promedio de calificaciones
                                    </Typography>
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48, mr: 2 }}>
                                            <StarIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h5">{stats.avg}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                (de {stats.min} a {stats.max})
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Total de reseñas
                                    </Typography>
                                    <Box display="flex" alignItems="center" mt={1}>
                                        <Chip label={stats.total} color="info" />
                                        <Box ml={2}>
                                            <Typography variant="body2">Reseñas registradas</Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Rango de calificación
                                    </Typography>
                                    <Box mt={1}>
                                        <Typography variant="h6">
                                            {stats.min} — {stats.max}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Mínimo y máximo observados
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Paper elevation={1}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Código cliente</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Puntuación</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Comentario</TableCell>
                                        <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {visits.map((v) => (
                                        <TableRow key={v.id} hover>
                                            <TableCell>{v.visitDate}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {v.clientId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{v.clientName}</TableCell>
                                            <TableCell>
                                                <Chip label={v.score ?? "—"} color="info" size="small" />
                                            </TableCell>
                                            <TableCell>{v.comment ?? "—"}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => {
                                                        if (v.ratingId) navigate(`/on-six/rating/${v.ratingId}`);
                                                    }}
                                                >
                                                    Ver calificación
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {visits.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Typography variant="body2" align="center" color="text.secondary" sx={{ py: 3 }}>
                                                    No hay visitas para mostrar.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </div>

            {/* <div className="col-span-2 h-screen sticky top-0 border-l-2 border-solid border-light overflow-hidden p-4">
            </div> */}
        </div>
    );
};

export default ProductDetailReport;